import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// P1-1: while impersonating, the frontend attaches an X-Impersonated-Role
// header so the backend can enforce view-only mode. Header attach is done
// here so every consumer (react-query, fetch, etc.) gets it via axios.
function _setImpersonationHeader(role) {
  if (role) {
    api.defaults.headers.common["X-Impersonated-Role"] = role;
  } else {
    delete api.defaults.headers.common["X-Impersonated-Role"];
  }
}

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null); // null = loading, false = unauth, obj = auth
  const [impersonatedRole, setImpersonatedRole] = useState(null);
  const [impersonationExpiresAt, setImpersonationExpiresAt] = useState(null);
  const [checked, setChecked] = useState(false);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setRealUser(data);
    } catch {
      setRealUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("hcd_token", data.access_token);
    setRealUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("hcd_token");
    endImpersonation();
    setRealUser(false);
  };

  const impersonate = (role, seconds = 30) => {
    if (realUser?.role !== "it_admin") return;
    setImpersonatedRole(role);
    _setImpersonationHeader(role);
    const exp = Date.now() + seconds * 1000;
    setImpersonationExpiresAt(exp);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setImpersonatedRole(null);
      _setImpersonationHeader(null);
      setImpersonationExpiresAt(null);
    }, seconds * 1000);
  };

  const endImpersonation = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setImpersonatedRole(null);
    _setImpersonationHeader(null);
    setImpersonationExpiresAt(null);
  };

  // The `user` exposed to the app blends real identity with impersonation.
  const user = realUser && impersonatedRole
    ? { ...realUser, role: impersonatedRole, roles: [impersonatedRole], _impersonated: true }
    : realUser;

  return (
    <AuthContext.Provider value={{
      user, realUser, checked, login, logout, refresh,
      impersonate, endImpersonation, impersonatedRole, impersonationExpiresAt,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
